import { useState } from "react";
import { Link } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { hint as lookup } from "@/lib/glossary";
import { deep as lookupDeep } from "@/lib/deepGlossary";
import { Info } from "lucide-react";

const Deep = ({ d }) => (
  <div className="space-y-1.5 text-[11px] leading-snug text-slate-300" data-testid="hint-deep">
    {d.why && <div><span className="text-cyan-300 font-semibold">Why it matters: </span>{d.why}</div>}
    {d.how && <div><span className="text-cyan-300 font-semibold">How it works: </span>{d.how}</div>}
    {d.action && <div><span className="text-cyan-300 font-semibold">What to do with it: </span>{d.action}</div>}
    {d.example && <div className="text-slate-400 italic">Example: {d.example}</div>}
  </div>
);

// Hover/focus popup that explains an option. Use `k` for a glossary key or `title`/`text` inline.
// asChild: pass the child element directly as the trigger (native elements / forwardRef components).
// When a deep explanation exists for `k` (or `deepKey`), the popup shows What / Why / How / What to do + example.
export const Hint = ({ k, deepKey, title, text, side = "top", align = "center", asChild = false, className, children, testid }) => {
  const entry = k ? lookup(k) : null;
  const d = lookupDeep(deepKey || k);
  const t = title ?? entry?.[0];
  const body = text ?? entry?.[1];
  if (!body) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {asChild ? children : <span className={className || "inline-flex"}>{children}</span>}
      </TooltipTrigger>
      <TooltipContent
        side={side}
        align={align}
        sideOffset={8}
        collisionPadding={12}
        data-testid={testid || (k ? `hint-${k}` : undefined)}
        className={`apx-hint ${d ? "max-w-[340px]" : "max-w-[260px]"} px-3 py-2.5 rounded-lg text-left`}
      >
        {t && <div className="font-display text-[12px] font-semibold text-cyan-200 mb-0.5">{t}</div>}
        <div className="text-[11px] leading-snug text-slate-300">{d ? <><span className="text-cyan-300 font-semibold">What it is: </span>{body}</> : body}</div>
        {d && <div className="mt-1.5 pt-1.5 border-t border-slate-800"><Deep d={d} /></div>}
        <div className="mt-1.5 text-[10px] text-slate-500">Educational information, not financial advice.</div>
      </TooltipContent>
    </Tooltip>
  );
};

// Small-caps label with a subtle info marker; hover anywhere on it for the explanation.
export const HintLabel = ({ k, deepKey, title, text, children, className = "" }) => {
  if (!text && !(k && lookup(k))) return children;
  return (
    <Hint k={k} deepKey={deepKey} title={title} text={text} className={`inline-flex items-center gap-1 cursor-help ${className}`}>
      <span className="border-b border-dotted border-slate-600/80">{children}</span>
      <Info className="w-2.5 h-2.5 text-slate-600" aria-hidden />
    </Hint>
  );
};

// Clickable ⓘ icon: tap/click opens a dialog with the full explanation (works on phones where hover doesn't exist).
export const InfoIcon = ({ k, deepKey, title, text, className = "", testid }) => {
  const [open, setOpen] = useState(false);
  const entry = k ? lookup(k) : null;
  const d = lookupDeep(deepKey || k);
  const t = title ?? entry?.[0];
  const body = text ?? entry?.[1];
  if (!body) return null;
  return (
    <>
      <button type="button" aria-label={`What is ${t}?`} data-testid={testid || `info-${k || "inline"}`} onClick={(e) => { e.stopPropagation(); setOpen(true); }}
              className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-slate-500 hover:text-cyan-300 align-middle ${className}`}>
        <Info className="w-3.5 h-3.5" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-[#0F141C] border-slate-800 text-slate-100" data-testid={`info-dialog-${k || "inline"}`}>
          <DialogHeader><DialogTitle className="font-display">{t}</DialogTitle><DialogDescription className="text-slate-300 text-sm"><span className="text-cyan-300 font-semibold">What it is: </span>{body}</DialogDescription></DialogHeader>
          {d && <div className="text-sm"><Deep d={d} /></div>}
          <div className="text-[11px] text-slate-500 flex flex-wrap justify-between gap-2"><span>Educational information only — not financial advice. Predictions can be wrong; past results don't guarantee future outcomes.</span><Link to={k ? `/glossary#term-${k}` : "/glossary"} onClick={() => setOpen(false)} className="text-cyan-300 hover:underline whitespace-nowrap" data-testid={`${testid || `info-${k || "inline"}`}-all-terms`}>{k ? "See this term in the glossary →" : "See all terms →"}</Link></div>
        </DialogContent>
      </Dialog>
    </>
  );
};
